import { useLocation, useNavigate } from "react-router-dom";
import BreadCumb from "../../components/common/Breadcrumb";
import backArrow from "../../assets/task/back_arrow.svg";
import TeamAvailabilityCalendar from "../../components/resourceOverview/TeamAvailabilityCalendar";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import DateSelector from "../../components/common/DateSelector";
import { format, isBefore, parseISO } from "date-fns";
import { showError } from "../../components/common/ToastNotification";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useLazyQuery } from "@apollo/client";
import { EMPLOYEES_BY_AGENCY_ID, GET_MEETINGS_FOR_TASK_AVAILABILITY, GET_TASKS_FOR_TASK_AVAILABILITY, GET_TIME_AVAILABILITY_BY_EMPLOYEE_ID, LEAVES_BY_EMPLOYEE } from "../../api/query";
import Loader from "../../components/common/Loader";
import { useAuth } from "../../context/AuthContext";
import SearchableInputsDropdown from "../../components/common/SearchableDropdown";
import { formatHoursToHM } from "./ResourceOverview";

interface SelectedDate {
    date: Date;
    isHalfDay: boolean;
    totalAllocatedHours?: any;
}

const teamAvailabilitySchema = yup.object().shape({
    start_date: yup.date().required("This field is required"),
    end_date: yup.date().required("This field is required"),
    employeeID: yup.string().required("This field is required"),
    employeeName: yup.string().required("This field is required"),
});

const TeamAailability = () => {
    const { userInfo } = useAuth()
    const agencyId = userInfo?.agencies?.id

    const navigate = useNavigate();
    const [selectedDates, setSelectedDates] = useState<SelectedDate[]>([]);
    const location = useLocation()

    const [employeeOptions, setEmployeeOptions] = useState<{ key: string; name: string }[]>([]);

    const state = location.state as {
        resStartDate: string,
        resEndDate: string,
        resSelectedEmployeeID: any,
        resSelectedEmployeeName: string
    };

    const { resStartDate, resEndDate, resSelectedEmployeeID, resSelectedEmployeeName } = state

    //api
    const [
        getEmployeesByAgencyID,
        { data: employeesData, loading: employeesDataLoading },
    ] = useLazyQuery(EMPLOYEES_BY_AGENCY_ID, {
        fetchPolicy: 'no-cache'
    });

    const [getTimeAvailability, { data: timeAvailabilityData, loading: timeAvailabilityDataLoading }] = useLazyQuery(GET_TIME_AVAILABILITY_BY_EMPLOYEE_ID, {
        fetchPolicy: 'no-cache'
    })

    const [
        getLeavesByEmployee,
        { data, loading },
    ] = useLazyQuery(LEAVES_BY_EMPLOYEE, {
        fetchPolicy: 'no-cache'
    });

    const [getTasks, { data: employeeTaskListData, loading: employeeTaskListDataLoading }] = useLazyQuery(GET_TASKS_FOR_TASK_AVAILABILITY, {
        fetchPolicy: 'no-cache'
    })

    const [getMeetings, { data: meetingsData, loading: meetingsDataLoading }] = useLazyQuery(GET_MEETINGS_FOR_TASK_AVAILABILITY, {
        fetchPolicy: 'no-cache'
    })

    const leaveData = data && data?.leaves_by_employee?.leaves
    const taskData = employeeTaskListData && employeeTaskListData?.employee_tasks_list?.tasks
    const meetings = meetingsData?.get_meetings?.meetings?.filter(
        (meeting: any) => meeting.is_deleted === false
    );
    const timeData = timeAvailabilityData && timeAvailabilityData?.get_time_availability_by_employee_id

    useEffect(() => {
        if (!filterStartDate) {
            if (resStartDate && resEndDate && resSelectedEmployeeID && resSelectedEmployeeName) {
                localStorage.setItem("teamAvailabilityStartDate", resStartDate);
                localStorage.setItem("teamAvailabilityEndDate", resEndDate);
                localStorage.setItem("teamAvailabilityEmployeeID", resSelectedEmployeeID);
                localStorage.setItem("teamAvailabilityEmployeeName", resSelectedEmployeeName);
            }
        }
    }, [resStartDate, resEndDate, resSelectedEmployeeID, resSelectedEmployeeName])

    useEffect(() => {
        if (agencyId) {
            getEmployeesByAgencyID({
                variables: {
                    agency_id: parseInt(agencyId)
                }
            });
        }
    }, [agencyId])

    const filterStartDate = localStorage.getItem("teamAvailabilityStartDate");
    const filterEndDate = localStorage.getItem("teamAvailabilityEndDate");
    const filterEmployeeID = localStorage.getItem("teamAvailabilityEmployeeID");
    const filterEmployeeName = localStorage.getItem("teamAvailabilityEmployeeName");


    const {
        setValue,
        formState: { errors },
        watch,
        trigger,
        control
    } = useForm<any>({
        resolver: yupResolver(teamAvailabilitySchema),
        defaultValues: {
            end_date: filterEndDate || resEndDate || '',
            start_date: filterStartDate || resStartDate || '',
            employeeID: filterEmployeeID || resSelectedEmployeeID || '',
            employeeName: filterEmployeeName || resSelectedEmployeeName || ''
        }
    });

    //watch
    const watchStartDate = watch("start_date")
    const watchEndDate = watch("end_date")
    // const watchAssignedEmployee = watch("employeeName");
    const watchAssignedMemberID = watch('employeeID');

    useEffect(() => {
        setSelectedDates([]);
    }, [watchAssignedMemberID, watchStartDate, watchEndDate])

    useEffect(() => {
        if (employeesData && employeesData.employees_by_agency_id) {
            const employeeOptions = employeesData?.employees_by_agency_id?.employees?.map((emp: any) => ({
                key: emp.employee_id,
                name: `${emp.first_name} ${emp.last_name}`
            }));
            setEmployeeOptions(employeeOptions);
        }
    }, [employeesData])

    useEffect(() => {
        if (watchAssignedMemberID && watchStartDate && watchEndDate) {
            getTimeAvailability({
                variables: {
                    employee_id: parseInt(watchAssignedMemberID),
                    start_date: watchStartDate,
                    end_date: watchEndDate
                }
            })

            getLeavesByEmployee({
                variables: {
                    employee_id: watchAssignedMemberID,
                    start_date: `${watchStartDate}T00:00:00`,
                    end_date: `${watchEndDate}T23:59:59`
                }
            })
        }
    }, [watchStartDate, watchEndDate, watchAssignedMemberID])

    useEffect(() => {
        if (selectedDates?.length > 0) {
            const selectedDate = format(selectedDates[0].date, 'yyyy-MM-dd');

            getTasks({
                variables: {
                    employee_id: parseInt(watchAssignedMemberID),
                    start_date: selectedDate,
                    end_date: selectedDate
                }
            });

            getMeetings({
                variables: {
                    agency_id: parseInt(agencyId),
                    employee_id: parseInt(watchAssignedMemberID),
                    start_date: `${selectedDate}T00:00:00`,
                    end_date: `${selectedDate}T23:59:59`
                }
            });
        }
    }, [selectedDates, watchAssignedMemberID])

    const toggleDate = (date: Date, totalAllocatedHours: any) => {
        // If the same date is clicked again, unselect it
        // if (selectedDates.length > 0 && selectedDates[0].date.toDateString() === date.toDateString()) {
        //     setSelectedDates([]);
        //     return;
        // }

        // Otherwise, select only this date (replace previous)
        setSelectedDates([{ date, isHalfDay: false, totalAllocatedHours }]);
    };

    return (
        <>
            <form id="cts-respon-p-dv" className="w-full  relative  overflow-hidden flex flex-row items-start justify-center flex-wrap content-start py-40 px-20 mq991:p-8 mq575:p-4  box-border text-left text-base text-gray font-manrope">
                <div className="min-w-full flex flex-col items-start justify-start gap-[24px] ">
                    <BreadCumb />
                    <div className="self-stretch flex flex-row items-center justify-start gap-[5px] text-2xl">
                        <img className="w-6 relative h-6 overflow-hidden shrink-0 cursor-pointer dark-white-img" alt="" src={backArrow}
                            onClick={() => navigate(-1)}
                        />
                        <h3 className="m-0 flex-1 relative text-inherit font-bold font-inherit inline-block max-w-full mq450:text-base dark:text-white">Team Availability</h3>
                    </div>
                    <div className="m-0 self-stretch rounded-radius-radius-lg bg-surface-bg-blue darkborder-b-remove flex flex-col items-start justify-start p-8 mq575:p-3 box-border gap-spacing-lg max-w-full mq825:pt-5 mq825:pb-5 mq825:box-border mq1425:pt-[21px] mq1425:pb-[21px] mq1425:box-border">
                        <div className="self-stretch grid items-start gap-6">
                            <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 mt-[8px] gap-[8px] min-w-full">
                                <Controller
                                    name="start_date"
                                    control={control}
                                    render={({ field }) => (
                                        <DateSelector
                                            label="Start Date*"
                                            selectedDate={field.value}
                                            onChange={(date: string | any) => {
                                                const endDate = watch("end_date");
                                                const result = true;
                                                if (result) {
                                                    setSelectedDates([])
                                                    field.onChange(date);
                                                    setValue("end_date", null);
                                                    setValue("start_date", date);
                                                    trigger("start_date");
                                                    localStorage.setItem("teamAvailabilityStartDate", date);
                                                }
                                            }}
                                            nothideParsedValue={true}
                                            blueIcon={true}
                                            error={errors.start_date?.message}
                                            showAllDate={true}
                                        />
                                    )}
                                />
                                <Controller
                                    name="end_date"
                                    control={control}
                                    render={({ field }) => (
                                        <DateSelector
                                            label="End Date*"
                                            selectedDate={field.value}
                                            onChange={(date: string | null | any) => {
                                                const date2 = parseISO(watch("start_date"));
                                                const date1 = parseISO(date);
                                                if (isBefore(date1, date2)) {
                                                    showError(
                                                        "Please select date greater than start date."
                                                    );
                                                }
                                                // else if (differenceInDays(date1, date2) > 30) {
                                                //   showError(
                                                //     "The selected date range cannot exceed 31 days."
                                                //   );
                                                // } 
                                                else {
                                                    field.onChange(date);
                                                    setValue("end_date", date);
                                                    trigger("end_date");
                                                    localStorage.setItem("teamAvailabilityEndDate", date);
                                                }
                                            }}
                                            nothideParsedValue={true}
                                            blueIcon={true}
                                            dontopenDatepicker={!watch("start_date")}
                                            error={errors.end_date?.message}
                                            showAllDate={true}
                                        />
                                    )}
                                />
                                <SearchableInputsDropdown
                                    label="Employee*"
                                    initialValue={filterEmployeeName}
                                    options={employeeOptions}
                                    onSelect={(key: number, name: string) => {
                                        setValue('employeeID', key);
                                        setValue('employeeName', name);

                                        localStorage.setItem("teamAvailabilityEmployeeID", String(key));
                                        localStorage.setItem("teamAvailabilityEmployeeName", name);
                                    }}
                                    errorMessage={errors?.employeeName}
                                />
                            </div>
                            <div className="grid grid-cols-2 mq1900:grid-cols-2 mq1425:grid-cols-2 mq1275:grid-cols-2 mq950:grid-cols-2 mq825:grid-cols-1 mq575:grid-cols-1 min-w-full  gap-[16px]">
                                {/* Left Side */}
                                <div className=" bg-white flex items-start justify-center p-4 avibility_card_blg">
                                    {timeAvailabilityData && (
                                        <TeamAvailabilityCalendar
                                            selectedDates={selectedDates}
                                            onDateClick={toggleDate}
                                            startDate={watchStartDate ? new Date(watchStartDate) : null}
                                            endDate={watchEndDate ? new Date(watchEndDate) : null}
                                            timeData={timeData}
                                        />
                                    )}
                                </div>
                                {/* Right Side */}
                                <div className="flex flex-col items-start gap-4 text-left text-[20px] ">
                                    {/* leaves */}
                                    <div className="self-stretch shadow-[0px_0px_0px_rgba(0,_0,_0,_0.1)] rounded-[20px] bg-white overflow-hidden flex flex-col items-start p-4 gap-4 upcoming-card-top">
                                        <div className="self-stretch flex items-center">
                                            <h3 className="m-0 flex-1 relative text-inherit font-bold font-inherit inline-block max-w-full mq450:text-base dark:text-white">Time off</h3>
                                        </div>
                                        {leaveData && leaveData.length > 0 ? (
                                            <div className="grid gap-[16px] self-stretch max-h-[160px] overflow-y-auto">
                                                {leaveData.map((leave: any) => {
                                                    return (
                                                        <div className="self-stretch flex flex-col items-start gap-[6px] cleant-card-deta-dv">
                                                            <h4 className="self-stretch bg-white overflow-hidden flex flex-col items-start headingupcomming-dv">
                                                                <b className="relative">{leave?.employee_name}</b>
                                                            </h4>
                                                            <div className="self-stretch flex flex-col items-start text-num-16 text-dimgray-200">
                                                                <div className="self-stretch flex flex-col items-start justify-center gap-2">
                                                                    <div className="self-stretch flex items-center justify-between gap-0">
                                                                        <div className="relative font-semibold">{leave?.leave_type}</div>
                                                                        <div className="rounded-num-4 border-whitesmoke-200 upcoming-date-right flex flex-col items-start py-num-4 px-num-8 text-num-12">
                                                                            <b className="relative">{leave && format(leave?.start_date, 'MMM dd, yyyy')}</b>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        ) : (
                                            <div className="self-stretch flex items-center">
                                                No Leaves Taken
                                            </div>
                                        )}
                                    </div>
                                    {/* tasks */}
                                    <div className=" upcoming-card-bottom self-stretch flex-1 shadow-[0px_0px_0px_rgba(0,_0,_0,_0.1)] 
 rounded-[20px] bg-white overflow-hidden flex flex-col items-start p-4 gap-4">
                                        <div className="flex justify-between mq575:block w-full">
                                            <h4 className="self-stretch bg-white overflow-hidden flex flex-col items-start headingupcomming-dv mq575:mb-5">{selectedDates[0]?.date && format(selectedDates[0]?.date, 'MMM dd, yyyy')}</h4>
                                            <h4 className="self-stretch bg-white overflow-hidden flex flex-col items-start headingupcomming-dv">Allocated hours: {formatHoursToHM(selectedDates[0]?.totalAllocatedHours)}</h4>
                                            <h4 className="self-stretch bg-white overflow-hidden flex flex-col items-start headingupcomming-dv">Available hours: {formatHoursToHM(8 - selectedDates[0]?.totalAllocatedHours)}</h4>
                                        </div>
                                        <div className=" upcoming-card-bottom self-stretch flex-1 shadow-[0px_0px_0px_rgba(0,_0,_0,_0.1)] 
                                            rounded-[20px] bg-white overflow-hidden flex flex-col items-start p-4 gap-4 relative">
                                            <h4 className="self-stretch bg-white overflow-hidden flex flex-col items-start headingupcomming-dv meeting-headingupcomming-dv">Task list ({taskData?.length})</h4>
                                            <div className="grid gap-[16px] self-stretch max-h-[137px] overflow-y-auto mt-9">
                                                {taskData && taskData.length > 0 ? (
                                                    taskData?.map((task: any) => {
                                                        return (
                                                            <div className="light-bg-gray-dv flex-col self-stretch max-w-full">
                                                                <div className="grid gap-[3px] flex-col self-stretch max-w-full">
                                                                    <div>
                                                                        <p className="taskdetail-content my-0"><b>{task?.title}</b></p>
                                                                        <span className="taskdetail-subheading">{task?.hours}H {task?.mins}M</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )
                                                    })
                                                ) : (
                                                    <div className="light-bg-gray-dv flex-col self-stretch max-w-full">
                                                        No Tasks Available
                                                    </div>
                                                )}

                                            </div>
                                        </div>
                                        {/* meetings */}
                                        <div className=" upcoming-card-bottom self-stretch flex-1 shadow-[0px_0px_0px_rgba(0,_0,_0,_0.1)] 
                                            rounded-[20px] bg-white overflow-hidden flex flex-col items-start p-4 gap-4 relative">
                                            <h4 className="self-stretch bg-white overflow-hidden flex flex-col items-start headingupcomming-dv meeting-headingupcomming-dv">Meeting list ({meetings?.length})</h4>
                                            <div className="grid gap-[16px] self-stretch max-h-[137px] overflow-y-auto mt-9">
                                                {meetings && meetings.length > 0 ? (
                                                    meetings?.map((meeting: any) => {
                                                        return (
                                                            <div className="light-bg-gray-dv flex-col self-stretch max-w-full">
                                                                <div className="grid gap-[3px] flex-col self-stretch max-w-full">
                                                                    <p className="taskdetail-content my-0"><b>{meeting?.title}</b></p>
                                                                    <span className="taskdetail-subheading">{meeting && meeting?.duration}M</span>
                                                                </div>
                                                            </div>
                                                        )
                                                    })
                                                ) : (
                                                    <div className="light-bg-gray-dv flex-col self-stretch max-w-full">
                                                        No Meetings Available
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="w-[1728px] hidden flex-col items-center">
                                <div className="self-stretch relative bg-gainsboro h-px" />
                                <img className="w-6 relative h-6" alt="" />
                            </div>
                        </div>
                    </div>
                </div>
            </form >
            {(loading || employeeTaskListDataLoading || employeesDataLoading || timeAvailabilityDataLoading || meetingsDataLoading) && (
                <Loader />
            )}
        </>
    );
}

export default TeamAailability